// @ts-nocheck

import { useState } from 'react'
import { useDebouncedCallback } from 'use-debounce'
import { useDispatch, useSelector } from 'react-redux'
import { PaymentMethodEnum } from 'Constants/enums'
import {
  sendPaymentInfoEvent,
  sendPaymentCompletedEvent,
} from 'Redux/actions/googleAnalyticsActions'
import {
  getBluesnapPftoken,
  submitBluesnapPayment,
  submitBluesnapPaymentUsingShopperId,
} from 'Redux/actions/scheduleActions'
import {
  setPaypalPayment,
  getCampaigns as getAllCampaigns,
} from 'Redux/actions/campaignActions'

import {
  getAmountOfCampaigns,
  getCampaignToSchedule,
  getCampaigns,
} from 'Redux/selectors/campaigns'
import {
  getArtistBalance,
  getCountryName,
  getUserSelector,
} from 'Redux/selectors/auth'
import {
  getCoupon,
  getPftoken,
  getShopper,
} from 'Redux/selectors/scheduledCampaign'

import { CreditCard, CampaignPlatformTypeEnum } from 'Types/index'
import { countries3DS } from 'Constants/countriesList'
import { sendLogsToS3 } from 'Helpers/s3-logger'
import {
  purchaseEvent,
  paymentCompletedEvent,
  PurchaseTypeEnum,
} from 'Helpers/tagManager'
import { useTranslation } from 'react-i18next'
import { getUserInfo } from 'Redux/actions/auth'

declare const bluesnap

export const useCheckout = ({
  selectedDate,
  setIsLoading,
  setIsScheduleFinished,
}: {
  selectedDate?: string
  setIsLoading?: (value?: boolean) => void
  setIsScheduleFinished?: (value?: boolean) => void
}): {
  submitPayment: (
    bluesnapSecurityCountry?: string,
    bluesnapSecurityCardData?: CreditCard
  ) => void
  onSuccessPaypal: (payment: unknown) => void
  calculateTotalPrice: () => number
  isPaymentButtonLoading: boolean
  isPaymentPageLoading: boolean
  paymentError: string | null
  cardOption: string
  paymentOption: string
  closePaymentErrorAlert: () => void
  setCardHolderFirstName: (value: string) => void
  setCardHolderLastName: (value: string) => void
  setCardOption: (value: string) => void
  setPaymentOption: (value: string) => void
  setSelectedDate: (value: string) => void
  onClickPaypalEventSend: (campaignId: string | number, url: string) => void
} => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const amountOfCampaigns = useSelector(getAmountOfCampaigns)
  const country = useSelector(getCountryName)
  const coupon = useSelector(getCoupon)
  const balance = useSelector(getArtistBalance)
  const shopper = useSelector(getShopper)
  const user = useSelector(getUserSelector)
  const pfToken = useSelector(getPftoken)
  // const shopperToken3DS = useSelector(getShopperToken3DS)
  const campaigns = useSelector(getCampaigns)
  const campaignToSchedule = useSelector(getCampaignToSchedule)

  // Взять из формы карт
  const [cardOption, setCardOption] = useState(
    !shopper?.creditCards?.length && 'cardForm'
  )
  const [paymentOption, setPaymentOption] = useState('stripe')
  const [cardHolderFirstName, setCardHolderFirstName] = useState<string>('')
  const [cardHolderLastName, setCardHolderLastName] = useState<string>('')
  const [isPaymentButtonLoading, setIsPaymentButtonLoading] = useState(false)
  const [isPaymentPageLoading, setIsPaymentPageLoading] = useState(false)
  const [paymentError, setPaymentError] = useState<string | null>(null)

  const closePaymentErrorAlert = () => {
    setPaymentError('')
  }

  const getAndUpdatePfToken = () => {
    dispatch(
      getBluesnapPftoken((err, token) => {
        if (!err) {
          bluesnap.hostedPaymentFieldsUpdateToken(token.pfToken)
        }
      })
    )
  }

  const calculateTotalPrice = (): number => {
    const israelVat =
      country === 'IL' || country === 'ISR'
        ? campaignToSchedule.price * 0.17
        : 0
    const discount = coupon
      ? (campaignToSchedule.price + israelVat) * (coupon.discount / 100)
      : 0
    const resultPriceWithoutBalance =
      campaignToSchedule.price + israelVat - discount
    const resultPrice =
      resultPriceWithoutBalance > balance &&
      resultPriceWithoutBalance - balance >= 1
        ? resultPriceWithoutBalance - balance
        : 1
    return +resultPrice.toFixed(2)
  }

  const updateBalanceResult = () => {
    const israelVat =
      country === 'IL' || country === 'ISR'
        ? campaignToSchedule.price * 0.17
        : 0

    const discount = coupon
      ? (campaignToSchedule.price + israelVat) * (coupon.discount / 100)
      : 0
    const amount = calculateTotalPrice()

    return amount === 1
      ? balance - (campaignToSchedule.price - 1 + israelVat - discount)
      : 0
  }

  const handleBalanceUsed = () => {
    const currentBalance = updateBalanceResult()
    return balance - currentBalance
  }

  const handleScheduleEventSend = () => {
    const transactionTotal = calculateTotalPrice()

    const isFirstCampaign = !campaigns.find(
      ({ status: campaignStatus }) =>
        campaignStatus === 'scheduled' ||
        campaignStatus === 'running' ||
        campaignStatus === 'ended'
    )

    purchaseEvent({
      user,
      campaignId: campaignToSchedule.id,
      transactionTotal,
      price: campaignToSchedule.price,
      isFirstCampaign,
    })

    dispatch(
      sendPaymentCompletedEvent(
        campaignToSchedule.id,
        {
          price: campaignToSchedule.price,
          start_date: selectedDate,
        },
        window.location.href
      )
    )

    dispatch(getAllCampaigns())
    setIsLoading && setIsLoading(false)
    setIsScheduleFinished && setIsScheduleFinished(true)
  }

  const onSuccessPaypal = payment => {
    const transactionId = payment.purchase_units[0].payments.captures[0].id
    const transactionTotal =
      payment.purchase_units[0].payments.captures[0].amount
    const { create_time } = payment
    const amount = calculateTotalPrice()
    const {
      id: campaign_id,
      status,
      genres: genresArray,
      languages: languagesArray,
      price,
    } = campaignToSchedule

    const genresIds = genresArray.map(genre => genre.id)
    const languagesIds = languagesArray.map(language => language.id)
    status !== 'ended' &&
      dispatch(
        setPaypalPayment(
          transactionId,
          amount,
          create_time,
          user.country,
          [
            {
              type: CampaignPlatformTypeEnum.SPOTIFY,
              genres: genresIds,
              languages: languagesIds,
              startDate: selectedDate,
              isDanceRequested: false,
              comment: '',
              campaignId: Number(campaign_id),
              amount: price,
            },
          ],
          coupon && coupon.id,
          err => {
            if (!err) {
              paymentCompletedEvent({
                campaignId: campaignToSchedule.id,
                campaignNumber: amountOfCampaigns,
                campaignPlatformType: CampaignPlatformTypeEnum.SPOTIFY,
                campaignType: campaignToSchedule.type,
                campaignValue: campaignToSchedule.price,
                genresList: campaignToSchedule?.genres
                  .map(genre => genre.name)
                  .join(),
                languagesList: campaignToSchedule?.languages
                  .map(language => language.name)
                  .join(),
                paymentAmount: transactionTotal.value,
                paymentMethod: PaymentMethodEnum.PayPal,
                purchaseType: 'campaign-type'.includes('first')
                  ? PurchaseTypeEnum.FirstPurchase
                  : PurchaseTypeEnum.Purchase,
                transactionId,
                userId: user.id,
                userRole: user.role,
                is_feed: campaignToSchedule.is_feed || false,
              })
              handleScheduleEventSend()
              dispatch(getUserInfo())
            }
          }
        )
      )

    localStorage.setItem(
      'campaignIsScheduled',
      JSON.stringify('campaignIsScheduled')
    )
  }

  const onClickPaypalEventSend = (campaignId: number | string, url: string) => {
    dispatch(sendPaymentInfoEvent(campaignId, url))
  }

  const bluesnapPaymentCallback = error => {
    setIsPaymentPageLoading(true)
    if (!error) {
      handleScheduleEventSend()
      dispatch(getUserInfo())
    } else {
      getAndUpdatePfToken()
      setIsPaymentPageLoading(false)
      setIsPaymentButtonLoading(false)

      if (
        error?.error?.message &&
        error?.error?.message.length &&
        error?.error?.message[0]?.errorName
      ) {
        const errorCode = error.error.message[0].errorName
        setPaymentError(errorCode)
      } else {
        setPaymentError(
          t(
            'scheduleCampaignPage.paymentProcessingFailedPleaseTryWithDifferentPaymentMethodOrContactYourBankSupport'
          )
        )
      }
    }
  }

  const handleChargePayment = (
    amount: number,
    scheduledCampaign: TiktokCampaign | SpotifyCampaign,
    cardData: { cardLastFourDigits: string; cardType: string },
    threeDSecure,
    shopper: { firstName: string; lastName: string; country: string },
    subtotal: number,
    startDate: string
  ) => {
    dispatch(
      submitBluesnapPayment({
        amount,
        campaigns: [
          {
            type: CampaignPlatformTypeEnum.SPOTIFY,
            // @ts-ignore
            genres:
              scheduledCampaign.platformType === CampaignPlatformTypeEnum.TIKTOK
                ? scheduledCampaign.genres.map(genre => genre.value)
                : [],
            languages:
              scheduledCampaign.platformType === CampaignPlatformTypeEnum.TIKTOK
                ? scheduledCampaign.languages.map(language => language.value)
                : [],
            amount: subtotal,
            startDate,
            isDanceRequested:
              scheduledCampaign.platformType ===
                CampaignPlatformTypeEnum.TIKTOK &&
              scheduledCampaign.isDanceRequested,
            comment:
              scheduledCampaign.platformType === CampaignPlatformTypeEnum.TIKTOK
                ? scheduledCampaign.comment
                : '',
            campaignId: Number(scheduledCampaign.id),
          },
        ],
        country: shopper.country,
        couponId: coupon?.id,
        pfToken,
        firstName: shopper.firstName,
        lastName: shopper.lastName,
        cardLastFourDigits: cardData.cardLastFourDigits,
        cardType: cardData.cardType,
        callback: bluesnapPaymentCallback,
        isNewCard: false,
        threeDSecure,
      })
    )
  }

  const onBluesnapPaymentClick = async (
    bluesnapSecurityCountry?: string,
    bluesnapSecurityCardData?: CreditCard
  ) => {
    const THREE_D_S_MIN_AMOUNT = 30
    let last_name
    let first_name

    const amount = calculateTotalPrice()
    const transactionBalance = updateBalanceResult()
    const balanceUsedThisTime = handleBalanceUsed()

    // const saveAsDefault = paymentForm ? paymentForm.saveAsDefault : null

    setIsPaymentButtonLoading(true)
    dispatch(sendPaymentInfoEvent(campaignToSchedule.id, window.location.href))

    if (shopper && shopper.cardHolderLastName && shopper.cardHolderFirstName) {
      last_name =
        shopper.cardHolderLastName && shopper.cardHolderLastName.toUpperCase()
      first_name =
        shopper.cardHolderFirstName && shopper.cardHolderFirstName.toUpperCase()
    } else if (cardHolderLastName && cardHolderFirstName) {
      last_name = cardHolderLastName.toUpperCase()
      first_name = cardHolderFirstName.toUpperCase()
    } else {
      last_name = ''
      first_name = ''
    }

    // 3ds conditions
    const amount_condition = amount > THREE_D_S_MIN_AMOUNT
    const user_country_condition =
      bluesnapSecurityCountry !== (country && country.toUpperCase())
    const compulsory_countries_condition = countries3DS.includes(
      bluesnapSecurityCountry
    )
    const card_holder_first_name_condition =
      first_name !== (user.first_name && user.first_name.toUpperCase())
    const card_holder_last_name_condition =
      last_name !== (user.last_name && user.last_name.toUpperCase())

    // тут нужно понять что он вводит карту, а не выбирает существующую
    const shopper_with_new_card_condition = shopper && cardOption === 'cardForm'

    const threeDSecureObj =
      user_country_condition ||
      compulsory_countries_condition ||
      amount_condition ||
      card_holder_first_name_condition ||
      card_holder_last_name_condition ||
      shopper_with_new_card_condition
        ? { amount, currency: 'USD' }
        : null

    if (cardOption === 'cardForm' || !shopper) {
      if (threeDSecureObj) {
        //  eslint-disable-next-line @typescript-eslint/no-explicit-any
        bluesnap.hostedPaymentFieldsSubmitData((callback: any) => {
          setIsPaymentPageLoading(true)
          if (callback.cardData != null) {
            sendLogsToS3(
              amount,
              transactionBalance,
              balanceUsedThisTime,
              campaignToSchedule,
              callback,
              'new shopper + 3DS',
              null
            )
            if (
              callback.threeDSecure &&
              callback.threeDSecure.authResult === 'AUTHENTICATION_FAILED'
            ) {
              setIsPaymentPageLoading(false)
              setIsPaymentButtonLoading(false)
              return
            }

            handleChargePayment(
              amount,
              campaignToSchedule,
              {
                cardLastFourDigits: callback.cardData.last4Digits,
                cardType: callback.cardData.ccType,
              },
              null,
              {
                firstName: first_name,
                lastName: last_name,
                country: bluesnapSecurityCountry,
              },
              campaignToSchedule.price,
              selectedDate
            )
          } else {
            const errorArray = callback.error

            if (errorArray.errorCode === 14101) {
              localStorage.removeItem('campaignIsScheduled')
              setPaymentError('Try to pay using PayPal')
              getAndUpdatePfToken()
              setIsPaymentPageLoading(false)
              setIsPaymentButtonLoading(false)
            } else {
              setPaymentError('Try a different payment method')
              getAndUpdatePfToken()
              setIsPaymentPageLoading(false)
              setIsPaymentButtonLoading(false)
            }
          }
        }, threeDSecureObj)
      } else {
        setIsPaymentButtonLoading(true)

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        bluesnap.hostedPaymentFieldsSubmitData((callback: any) => {
          setIsPaymentPageLoading(true)

          if (callback.cardData != null) {
            sendLogsToS3(
              amount,
              transactionBalance,
              balanceUsedThisTime,
              campaignToSchedule,
              callback,
              'new shopper - 3DS',
              callback.cardData.binCategory
            )

            // submitBluesnapPayment(
            //   pfToken,
            //   amount,
            //   cardHolderFirstName,
            //   cardHolderLastName,
            //   saveAsDefault,
            //   campaignToSchedule.id,
            //   callback.cardData.last4Digits,
            //   callback.cardData.ccType,
            //   country,
            //   transactionBalance,
            //   balanceUsedThisTime,
            //   campaignToSchedule.price,
            //   callback,
            //   bluesnapPaymentCallback
            // )
            handleChargePayment(
              amount,
              campaignToSchedule,
              {
                cardLastFourDigits: callback.cardData.last4Digits,
                cardType: callback.cardData.ccType,
              },
              null,
              {
                firstName: first_name,
                lastName: last_name,
                country: bluesnapSecurityCountry,
              },
              campaignToSchedule.price,
              selectedDate
            )
          } else {
            setIsPaymentPageLoading(false)
            setIsPaymentButtonLoading(false)
          }
        })
      }
    } else if (shopper) {
      const number_of_cards = shopper.creditCards.length

      if (threeDSecureObj || number_of_cards > 1) {
        bluesnap.threeDsPaymentsSetup(shopper.shopperToken3DS, sdkResponse => {
          const { code } = sdkResponse

          setIsPaymentPageLoading(true)
          if (Number(code) === 1) {
            //  eslint-disable-next-line @typescript-eslint/no-explicit-any
            localStorage.setItem('campaignIsScheduled', true as any)

            const { cardData } = sdkResponse
            const { threeDSecure } = sdkResponse
            const amount = calculateTotalPrice()
            const transactionBalance = updateBalanceResult()
            const balanceUsedThisTime = handleBalanceUsed()

            sendLogsToS3(
              amount,
              transactionBalance,
              balanceUsedThisTime,
              campaignToSchedule,
              sdkResponse,
              'shopper + 3DS',
              cardData.last4Digits
            )

            // dispatch(
            //   submitBluesnapPaymentUsingShopperId(
            //     amount,
            //     campaignToSchedule.id,
            //     cardData.last4Digits,
            //     cardData.ccType,
            //     cardData.issuingCountry.toUpperCase(),
            //     transactionBalance,
            //     balanceUsedThisTime,
            //     threeDSecure,
            //     campaignToSchedule.price,
            //     cardData,
            //     bluesnapPaymentCallback
            //   )
            // )
            handleChargePayment(
              amount,
              campaignToSchedule,
              {
                cardLastFourDigits: cardData.last4Digits,
                cardType: cardData.ccType,
              },
              threeDSecure,
              {
                firstName: first_name,
                lastName: last_name,
                country: bluesnapSecurityCountry,
              },
              campaignToSchedule.price,
              selectedDate
            )
          } else {
            setIsPaymentPageLoading(false)
            setIsPaymentButtonLoading(false)
          }
        })
        const cardDataWithNewAmount = Object.assign(bluesnapSecurityCardData, {
          amount,
        })
        bluesnap.threeDsPaymentsSubmitData(cardDataWithNewAmount)
      } else {
        setIsPaymentPageLoading(true)
        const creditCard = shopper.creditCards.find(
          card =>
            //  eslint-disable-next-line @typescript-eslint/ban-ts-comment
            //  @ts-ignore: Unreachable code error: comment
            card.cardLastFourDigits === paymentForm.cardOptions
        )

        submitBluesnapPaymentUsingShopperId(
          amount,
          campaignToSchedule.id,
          creditCard?.cardLastFourDigits,
          creditCard?.cardType,
          country,
          transactionBalance,
          balanceUsedThisTime,
          false,
          campaignToSchedule.price,
          creditCard,
          bluesnapPaymentCallback
        )
      }
    }
  }

  const [debouncedBluesnapPayment] = useDebouncedCallback(
    onBluesnapPaymentClick,
    1000
  )

  return {
    submitPayment: debouncedBluesnapPayment,
    onSuccessPaypal,
    calculateTotalPrice,
    isPaymentButtonLoading,
    isPaymentPageLoading,
    paymentError,
    cardOption,
    paymentOption,
    closePaymentErrorAlert,
    setCardHolderFirstName,
    setCardHolderLastName,
    setCardOption,
    setPaymentOption,
    onClickPaypalEventSend,
  }
}
